// @ts-strict-ignore
import { useEffect, useState } from "react";

import { allowedMarketoKeys, marketoParamsDefault } from "./handleMarketoQueryParams";
import type { MarketoParams, UtmAttributionFields } from "./types";

declare global {
  interface Window {
    brz: {
      attrib: {
        processAttributionDone: boolean;
        retrieveFieldsValues: () => Promise<UtmAttributionFields>;
      };
    };
  }
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const attributionUrl =
  new URL(window.location.href).hostname === "try.braze.com"
    ? "https://www.braze.com/scripts/ma-sync.js"
    : "https://web-dev.braze-dev.com/scripts/ma-sync.js";

const MAX_RETRIES = 30;

export const useLoadUtmAttributionScript = () => {
  const [utmReady, setUtmReady] = useState(false);
  const [utmFailed, setUtmFailed] = useState(false);

  useEffect(() => {
    const attributionScript = document.createElement("script");

    attributionScript.src = attributionUrl;
    attributionScript.type = "text/javascript";
    attributionScript.async = true;

    attributionScript.onload = () => {
      setUtmReady(true);
    };

    attributionScript.onerror = () => {
      setUtmFailed(true);
    };

    document.head.appendChild(attributionScript);

    return () => {
      document.head.removeChild(attributionScript);
    };
  }, []);

  return { utmReady, utmFailed };
};

export const UpdateUtmAttributionValues = async (
  marketoParams: React.RefObject<MarketoParams>,
  maxUtmRetries = MAX_RETRIES
): Promise<MarketoParams> => {
  const utmMarketoParams = {
    ...marketoParamsDefault,
    ...marketoParams.current,
  };
  const updateUtmInfo = async (marketoParams, currentAttemp = 0) => {
    try {
      if (currentAttemp >= maxUtmRetries) {
        console.warn("Error retrieving UTM Attribution value");
        return utmMarketoParams;
      }
      if (window.brz?.attrib?.processAttributionDone) {
        const marketoAttributeStorage = await window.brz.attrib.retrieveFieldsValues();
        // Sanitize the marketoAttributeStorage object
        for (const [key, value] of Object.entries(marketoAttributeStorage)) {
          if (!allowedMarketoKeys.includes(key) || !(typeof value === "string")) {
            delete marketoAttributeStorage[key];
          }
        }
        return {
          ...marketoParamsDefault,
          ...marketoAttributeStorage,
          ...marketoParams.current,
        };
      }
      await delay(1000);
      return updateUtmInfo(marketoParams, currentAttemp + 1);
    } catch (e) {
      console.warn("Error retrieving UTM Attribution value", e);
      return utmMarketoParams;
    }
  };
  return updateUtmInfo(marketoParams);
};
